// mailListConversation.ts — Cluster POSTFACH: aufklappbare Konversationen (P7)
//
// `groupConversations` (graphMailService) faltet einen Thread auf EINE Zeile
// zusammen. Sichtbar bleibt davon nur die neueste Nachricht — dass zwei oder
// zwanzig weitere dahinterstehen, sah man bisher nicht und kam auch nicht an
// sie heran (Serienmails/Rundschreiben verschwanden faktisch).
//
// Hier liegt die reine Logik: wie viele Nachrichten hinter einer Zeile stecken,
// welche Zeile sich überhaupt aufklappen lässt und wie die nachgeladenen
// Nachrichten unter der Hauptzeile stehen. Bewusst ohne React — der
// Aufklapp-Zustand lebt in der Komponente, die Regeln sind hier einzeln prüfbar.

import type { Email, MailFolder } from "../../services/graphMailService";
import { standardAliasOf } from "./mailFolderTree";

/**
 * Nachrichten hinter einer Listenzeile — die Zeile selbst, die auf dieser Seite
 * gefalteten und die, die nur der Thread-Index kennt.
 *
 * `extern` sind die Antworten aus „Gesendet". Ohne sie zählte die Zeile nur den
 * geladenen Ordnerausschnitt, und der ist fast immer eine einzige Nachricht:
 * Am Ordner „Erneut erinnern aktiviert" (cll@courte.com, 31.08.2026) gemessen
 * zeigten 47 von 61 Zeilen deshalb GAR KEINEN Pfeil, obwohl 2 bis 6 weitere
 * Nachrichten zum Thread gehörten. Die Zeile behauptete nicht „1 von 6" — sie
 * behauptete, es gäbe nichts aufzuklappen.
 */
export function nachrichtenAnzahl(email: Pick<Email, "thread">, extern = 0): number {
    return 1 + (email.thread?.length ?? 0) + Math.max(0, extern);
}

/**
 * Wie viele Nachrichten der Thread-Index für DIESE Zeile beisteuert.
 *
 * Der Index nennt die `internetMessageId`s aus „Gesendet". Die eigene Kopie
 * muss heraus: Bei einer Selbst-Weiterleitung ist die Mail in der Liste
 * dieselbe wie die in „Gesendet" — nur eine andere Kopie. Ohne diesen Abzug
 * bekäme jede selbst weitergeleitete Mail einen Aufklapp-Pfeil auf einen
 * Verlauf, der nur aus ihr selbst besteht. Im Postfach cll@courte.com wäre das
 * der Regelfall: 51 von 58 Mails eines Ordners kamen so zustande.
 */
export function externeAnzahl(
    email: Pick<Email, "internetMessageId">,
    ids: readonly string[] | undefined,
): number {
    if (!ids?.length) return 0;
    const eigene = email.internetMessageId;
    return eigene ? ids.filter((x) => x !== eigene).length : ids.length;
}

/** Nur eine Zeile mit mindestens einer weiteren Nachricht ist aufklappbar. */
export function istAufklappbar(email: Pick<Email, "thread">, extern = 0): boolean {
    return nachrichtenAnzahl(email, extern) > 1;
}

/**
 * Sobald die Konversation nachgeladen ist, zählt der geladene Stand: Graph
 * liefert auch Nachrichten aus anderen Ordnern (etwa die eigenen Antworten aus
 * „Gesendet"), die in der gruppierten Liste gar nicht vorkamen. Die Zahl an der
 * Zeile darf dann nicht kleiner sein als das, was darunter tatsächlich steht.
 *
 * Der geladene Stand schlägt den Index auch dann, wenn er KLEINER ist — er ist
 * die Wahrheit: der Index ist eine Untergrenze aus einem endlichen Fenster, und
 * er weiß nichts vom Papierkorb-Filter der Konversationsansicht.
 *
 * Seit der Outlook-Desktop-Darstellung stehen unter der Kopfzeile ALLE
 * Nachrichten — die neueste (= die Kopfzeile) eingeschlossen. `kinder` ist
 * damit schon die volle Anzahl.
 */
export function sichtbareAnzahl(
    email: Pick<Email, "thread">,
    kinder?: readonly Email[],
    extern = 0,
): number {
    return kinder ? Math.max(1, kinder.length) : nachrichtenAnzahl(email, extern);
}

/** Aufklappen/Zuklappen einer Konversation; liefert IMMER ein neues Set. */
export function schalteAufklappen(offen: ReadonlySet<string>, id: string): Set<string> {
    const next = new Set(offen);
    if (!next.delete(id)) next.add(id);
    return next;
}

/**
 * Die Nachrichten UNTER der Kopfzeile — wie in Outlook Desktop: ALLE
 * Nachrichten der Unterhaltung, neueste oben, die neueste (also die, die als
 * Kopfzeile steht) eingeschlossen. Die Kopfzeile ist die Unterhaltung, die
 * Zeilen darunter sind ihre einzelnen Nachrichten; erst dort lässt sich jede
 * davon — auch die eigenen Antworten aus „Gesendet" — einzeln anklicken.
 *
 * Doppelungen werden weiterhin abgewehrt: DIESELBE MAIL IN ZWEI KOPIEN.
 * Schickt jemand eine Mail an sich selbst, liegt sie zweimal im Postfach:
 * einmal in „Gesendete Elemente", Sekunden später als empfangene Kopie. Beide
 * haben eine eigene `id`, aber DIESELBE `internetMessageId`. Ohne diesen
 * Abgleich stand die Mail zweimal untereinander im Verlauf — an einem echten
 * Thread nachgemessen (01.09.2026): 8 Nachrichten, davon nur 5 verschiedene
 * Message-Ids.
 *
 * Bei einem Dublettenpaar gewinnt die Kopie der Kopfzeile (sie ist die, die
 * in der Liste schon ausgewählt sein kann), sonst die zuerst einsortierte,
 * also die neueste.
 *
 * Fehlt die Kopfzeile in der geladenen Konversation (Cache-Stand, Ordner-
 * Wechsel), wird sie ergänzt — unter der Kopfzeile darf nie weniger stehen
 * als in ihr.
 *
 * Nachrichten ohne `internetMessageId` (ältere Cache-Objekte, Demo-Postfach)
 * werden nie zusammengefasst: ohne Schlüssel ist „gleich" nicht feststellbar,
 * und eine zu volle Liste ist besser als eine, aus der etwas verschwindet.
 */
export function konversationsZeilen(alle: readonly Email[], haupt: Email): Email[] {
    const hauptKey = haupt.internetMessageId || null;
    const ohneHauptKopien = alle.filter(
        (e) => e.id !== haupt.id && !(hauptKey && e.internetMessageId === hauptKey),
    );
    const gesehen = new Set<string>();
    if (hauptKey) gesehen.add(hauptKey);
    const hauptInListe = alle.find((e) => e.id === haupt.id);
    return [hauptInListe ?? alsEinzelnachricht(haupt), ...ohneHauptKopien]
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .filter((e) => {
            if (e.id === haupt.id || !e.internetMessageId) return true;
            if (gesehen.has(e.internetMessageId)) return false;
            gesehen.add(e.internetMessageId);
            return true;
        });
}

/**
 * Die Kopfzeile einer Unterhaltung als EINE Nachricht — für den Lesebereich,
 * der wie in Outlook Desktop nur die ausgewählte Nachricht zeigt.
 *
 * `groupConversations` hängt an die Kopfzeile den Verlauf (`thread`) und
 * sammelt die Anhänge über den ganzen Thread (`hasAttachments`,
 * `attachmentMessageIds`). Im Lesebereich stünden sonst die älteren
 * Nachrichten wieder als Stapel unter der neuesten, und die Anhangleiste
 * zeigte Dateien, die gar nicht an dieser Nachricht hängen.
 *
 * Erzeugt ein NEUES Objekt — die Kopfzeile in der Liste bleibt, wie sie ist.
 */
export function alsEinzelnachricht(email: Email): Email {
    const ids = email.attachmentMessageIds;
    const eigeneAnhaenge = ids ? ids.includes(email.id) : !!email.hasAttachments;
    return {
        ...email,
        type: email.type === "conversation" ? "normal" : email.type,
        thread: undefined,
        hasAttachments: eigeneAnhaenge,
        attachmentMessageIds: eigeneAnhaenge ? [email.id] : [],
    };
}

/**
 * Die Ids der Ordner „Gesendete Elemente" eines Postfachs (oberste Ebene —
 * dort liegt der Standardordner; Unterordner sind Ablage, keine Sendung).
 */
export function gesendetOrdnerIds(baum: readonly MailFolder[] | undefined): Set<string> {
    const ids = new Set<string>();
    for (const ordner of baum ?? []) {
        if (standardAliasOf(ordner) === "sentitems") ids.add(ordner.id);
    }
    return ids;
}

/**
 * Ist diese Nachricht eine eigene, gesendete? Dann trägt ihre Zeile in der
 * aufgeklappten Unterhaltung den Hinweis „Gesendet" (wie in Outlook Desktop).
 *
 * Maßgeblich ist der Ordner: liegt die Nachricht in „Gesendete Elemente",
 * ist sie gesendet — gleich, wer als Absender drinsteht (Senden im Auftrag
 * eines geteilten Postfachs). Ohne Ordner-Angabe (Alt-Cache, Ordnerbaum noch
 * nicht geladen) greift der Absender: kommt die Nachricht vom Postfach
 * selbst, hat das Postfach sie gesendet.
 */
export function istGesendet(
    email: Pick<Email, "parentFolderId" | "senderEmail">,
    postfach: string | null | undefined,
    gesendetOrdnerIds: ReadonlySet<string>,
): boolean {
    if (email.parentFolderId && gesendetOrdnerIds.has(email.parentFolderId)) return true;
    if (!postfach || !email.senderEmail) return false;
    return email.senderEmail.trim().toLowerCase() === postfach.trim().toLowerCase();
}

/**
 * Beschriftung des Aufklapp-Schalters. Die Anzahl gehört hinein, weil der
 * Schalter für Screenreader sonst nur „Aufklappen" sagt und die eigentliche
 * Information — es steckt mehr dahinter — verloren geht.
 */
export function aufklappLabel(offen: boolean, anzahl: number): string {
    return offen
        ? `Konversation zuklappen (${anzahl} Nachrichten)`
        : `Konversation aufklappen (${anzahl} Nachrichten)`;
}
